import "core-js/stable/structured-clone";

import {
  batch,
  ComponentProps,
  createContext,
  createMemo,
  FlowProps,
  JSX,
  onCleanup,
  onMount,
  untrack,
  useContext,
} from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import {
  AnyZodObject,
  TypeOf,
  ZodAny,
  ZodDefault,
  ZodEffects,
  ZodError,
  ZodObject,
  ZodString,
} from "zod";

import assert from "@repo/utils/assert";
import Chainable from "@repo/utils/Chainable";
import UIterable from "@repo/utils/UIterable";
import UObject from "@repo/utils/UObject";
import UType from "@repo/utils/UType";
import UZod from "@repo/utils/UZod";
import UEventTarget from "@repo/utils-client/UEventTarget";

import createFocusOnMount from "./createFocusOnMount";

declare namespace createForm {
  type Schema = AnyZodObject | ZodEffects<AnyZodObject>;

  type Options<Schema extends createForm.Schema> = {
    schema: Schema;
    defaultFields?: Partial<Schema["_input"]> | null;
    autofocus?:
      | boolean
      // TODO: Support { field: ..., delay: ... }
      | keyof (Schema extends AnyZodObject
          ? Schema["shape"]
          : UType.Narrow<
              Schema,
              ZodEffects<AnyZodObject>
            >["_def"]["schema"]["shape"])
      | number;
  };

  type RegisterableElement = (typeof createForm.registerableElements)[number];

  type Register<Field extends string | Schema> = ReturnType<
    typeof createForm<
      Field extends string ? ZodObject<Record<Field, any>> : Field
    >
  >["register"];
}

createForm.registerableElements = ["input", "select", "textarea"] as const;

function createForm<Schema extends createForm.Schema>({
  schema,
  defaultFields = {},
  ...options
}: createForm.Options<Schema>) {
  const { autofocus } = { ...useContext(Context), ...options };

  const shape = createMemo(() =>
    schema instanceof ZodEffects ? schema._def.schema.shape : schema.shape
  );

  const initial = untrack(() => ({
    ...UObject.transform(shape(), ([key, value]) => {
      const defaultValue = (
        UObject.getInstanceOf(value, ZodDefault) ||
        UObject.getInstanceOf(
          UObject.getInstanceOf(value, ZodEffects)?._def.schema,
          ZodDefault
        )
      )?._def.defaultValue();

      return defaultValue != null && [key, defaultValue];
    }),
    ...(UObject.transform(
      defaultFields || ({} as UObject.Any),
      ([key, value]) =>
        value != null &&
        // Treat empty string as undefined to fail required validation
        value !== "" && [key, value]
    ) as {}),
  }));

  const [elements, setElements] = createStore<
    Partial<Record<keyof Schema["_input"], HTMLElement>>
  >({});

  const [fields, setFields] = createStore<Partial<Schema["_input"]>>(
    // Cannot use structuredClone on instance default field
    reconcile(initial)({})
  );

  const [errorByFieldName, setErrorByFieldName] = createStore<
    Partial<Record<keyof Schema["_input"], string>>
  >({});

  const [validByFieldName, setValidByFieldName] = createStore(
    UObject.transform(shape(), ([name, field]) => [
      name,
      (field as ZodAny).isOptional() || untrack(() => fields[name]),
    ]) as Record<keyof Schema["_input"], boolean>
  );

  let focused = false;

  const form = {
    reset: () => setFields(reconcile(initial)),
    inputs: fields,
    elements,
    parse: Object.assign(
      (transformSchema = (schema: Schema) => schema as AnyZodObject) => {
        const { success, data } = transformSchema(schema).safeParse(fields);
        return success ? (data as Schema["_output"]) : null;
      },
      {
        async async(transformSchema = (schema: Schema) => schema) {
          const { success, data } =
            await transformSchema(schema).safeParseAsync(fields);
          return success ? (data as Schema["_output"]) : null;
        },
      }
    ),
    submittable: () => Object.values(validByFieldName).every((valid) => valid),
    register: Chainable.create(
      Chainable.wrapFactory(
        ({ custom = false }) =>
          <Name extends UType.Narrow<keyof Schema["_input"], string>>(
            name: Name
          ) => {
            if (!(name in shape())) {
              throw new Error(`Schema does not include field "${name}"`);
            }

            const setValue = (value: Schema["_input"][Name] | undefined) => {
              setFields(
                // @ts-expect-error
                name,
                // Treat empty string as undefined to fail required validation
                value === "" ? undefined : value
              );
              (shape()[name] as ZodAny)
                .safeParseAsync(fields[name])
                .then((res) => {
                  setValidByFieldName(
                    // @ts-expect-error
                    name,
                    res.success
                  );
                  if (errorByFieldName[name])
                    setErrorByFieldName(
                      // @ts-expect-error
                      name,
                      res.success ? undefined : res.error.issues[0]?.message
                    );
                });
            };

            return {
              name,
              get value() {
                return (
                  UZod.findBase(shape()[name]) instanceof ZodString
                    ? // Prevent text inputs from showing "undefined"
                      fields[name] ?? ""
                    : fields[name]
                ) as Schema["_input"][Name] | undefined;
              },
              set value(value: Schema["_input"][Name] | undefined) {
                setValue(value);
              },
              get error() {
                return errorByFieldName[name];
              },
              ref(el) {
                setElements(
                  // @ts-expect-error
                  name,
                  el
                );
                onCleanup(() => {
                  setElements(
                    // @ts-expect-error
                    name,
                    undefined
                  );
                });

                if (
                  autofocus === name ||
                  ((autofocus === true || typeof autofocus === "number") &&
                    !focused)
                ) {
                  focused = true;
                  createFocusOnMount({
                    delay: typeof autofocus === "number" ? autofocus : 0,
                  })(el);
                }

                if (custom) return;

                // Attempt synchronously first to attach event listeners as early as possible
                if (!attempt())
                  onMount(() => {
                    if (!attempt())
                      throw new Error(
                        `Must register an registerable element or element containing a registerable element. Registerable elements include: ${createForm.registerableElements.join(", ")}. If this element doesn't contain a registerable field, use register.custom and manually set the value.`
                      );
                  });

                function attempt() {
                  const field = createForm.registerableElements.some(
                    (element) =>
                      el instanceof document.createElement(element).constructor
                  )
                    ? (el as HTMLElementTagNameMap[createForm.RegisterableElement])
                    : UIterable.firstDerived(
                        createForm.registerableElements,
                        (element) => el.querySelector(element)
                      );

                  if (!field) return;

                  // Adding event listeners instead of props allows unrelated props to be passed externally
                  const events = UEventTarget.wrap(field);

                  if (field.type === "file") {
                    // TODO: Check that schema for this field contains `zod.instanceof(File)`
                    return onCleanup(
                      events.on("change", async (e) => {
                        // Keep reference after defer
                        const currentTarget = e.currentTarget;

                        // @ts-expect-error
                        setValue(currentTarget.files?.[0] || undefined);
                      }).off
                    );
                  }

                  onCleanup(
                    events.on(["input", "change"], async (e) => {
                      // Keep reference after defer
                      const currentTarget = e.currentTarget;
                      // Wait for other event handlers to perform any processing
                      await new Promise(requestAnimationFrame);

                      // @ts-expect-error
                      setValue(currentTarget.value || undefined);
                    }).off
                  );

                  onCleanup(
                    events.on("blur", async () => {
                      (shape()[name] as ZodAny)
                        .safeParseAsync(fields[name])
                        .then((res) => {
                          setValidByFieldName(
                            // @ts-expect-error
                            name,
                            res.success
                          );
                          if (!res.success) {
                            setErrorByFieldName(
                              // @ts-expect-error
                              name,
                              res.error.issues[0]?.message
                            );
                          }
                        });
                    }).off
                  );
                  return field;
                }
              },
            } satisfies UObject.Override<
              ComponentProps<"input">,
              { ref: JSX.HTMLAttributes<HTMLElement>["ref"]; error?: string }
            >;
          }
      ),
      {
        custom: { custom: true },
      }
    ),
    validate: Object.assign(
      async () => {
        try {
          await form.validate.throws();
          return true;
        } catch (e) {
          return false;
        }
      },
      {
        async throws() {
          try {
            return await schema.parseAsync(fields);
          } catch (e) {
            assert(e instanceof ZodError);

            batch(() => {
              for (const issue of e.issues) {
                // @ts-expect-error
                setErrorByFieldName(issue.path.join("."), issue.message);
              }
            });

            throw new createForm.ValidationError(e.issues);
          }
        },
      }
    ),
    handleSubmit:
      <E,>(handler: (e: E, data: TypeOf<Schema>) => void) =>
      async (e: E) => {
        const res = await schema.safeParseAsync(fields);

        if (res.success) return handler(e, res.data);

        batch(() => {
          for (const issue of res.error.issues) {
            // @ts-expect-error
            setErrorByFieldName(issue.path.join("."), issue.message);
          }
        });
        throw new createForm.ValidationError(res.error.issues);
      },
    ValidationError: createForm.ValidationError as {
      new (
        issues: { path?: [keyof Schema["_input"]]; message: string }[]
      ): InstanceType<typeof createForm.ValidationError>;
    },
  };

  return form;
}

const Context = createContext<createForm.ProvideOptions.Options>();

declare namespace createForm.ProvideOptions {
  type Options = { autofocus?: boolean | number };
  type Props = FlowProps<Options>;
}

createForm.ProvideOptions = ({
  children,
  ...options
}: D<createForm.ProvideOptions.Props>) => (
  <Context.Provider value={options}>{children}</Context.Provider>
);

createForm.ValidationError = class extends Error {
  constructor(public issues: { path: (string | number)[]; message: string }[]) {
    super("Form validation failed.");
  }
};

export default createForm;
